import { Node } from "@tiptap/pm/model";
import { Editor, NodeViewWrapper } from "@tiptap/react";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { cn } from "../../../lib/utils/index.js";
import VideoPlayer from "./videojs/VideoPlayer.js";

interface VideoBlockViewProps {
  editor: Editor;
  getPos: () => number;
  node: Node & {
    attrs: {
      src: string;
      poster: string;
      assetId: string;
      playlistUrl?: string;
    };
  };
  updateAttributes: (attrs: Record<string, string>) => void;
}

export type Playlist = {
  id: string;
  title: string;
  description: string;
  poster: string;
  items: Item[];
};

export type Item = {
  src: string;
  type: string;
  label: string;
  width: number;
  height: number;
};

export const VideoBlockViewV1 = (props: VideoBlockViewProps) => {
  const { editor, getPos, node } = props;
  const videoWrapperRef = useRef<HTMLDivElement>(null);
  const { src, poster, playlistUrl, caption, title } = node.attrs;
  const [playlistContent, setPlaylistContent] = React.useState<
    Playlist | undefined
  >();
  const [error, setError] = React.useState("");

  useEffect(() => {
    async function getPlaylistContent() {
      try {
        const res = await fetch(playlistUrl, {
          cache: "no-cache",
        });
        return await res.json();
      } catch {
        setError("Issue with getting asset info");
        return undefined;
      }
    }

    if (playlistUrl) {
      try {
        getPlaylistContent()
          .then((data) => {
            setPlaylistContent(data);
          })
          .catch((err) => {
            setError(err);
          });
      } catch (error) {
        setError(error);
      }
    }
  }, [playlistUrl]);

  const wrapperClassName = cn(
    node.attrs.align === "left" ? "ml-0" : "ml-auto",
    node.attrs.align === "right" ? "mr-0" : "mr-auto",
    node.attrs.align === "center" && "mx-auto"
  );

  const onClick = useCallback(() => {
    editor.commands.setNodeSelection(getPos());
  }, [getPos, editor.commands]);

  const videoJsOptions = useMemo(() => {
    if (playlistContent == undefined) return {};

    if (
      playlistContent.items.some(
        (item) =>
          item.type === "application/x-mpegURL" ||
          item.type === "application/vnd.apple.mpegurl"
      )
    ) {
      return {
        sources: playlistContent.items
          .filter(
            (item) =>
              item.type === "application/x-mpegURL" ||
              item.type === "application/vnd.apple.mpegurl"
          )
          .map((item) => ({
            src: item.src,
            type: item.type,
            label: item.label,
          })),
        poster: playlistContent.poster,
      };
    }
    return {
      sources: playlistContent.items.map((item) => ({
        src: item.src,
        type: item.type,
        label: item.label,
      })),
      poster: playlistContent.poster,
    };
  }, [playlistContent]);
  return (
    <NodeViewWrapper>
      <div className={wrapperClassName} style={{ width: node.attrs.width }}>
        <div ref={videoWrapperRef}>
          {error && <div>{error}</div>}
          {/* <video
            controls
            className="block"
            src={src}
            poster={poster}
            title=""
            onClick={onClick}
          /> */}
          {playlistContent && <VideoPlayer options={videoJsOptions} />}
          {caption && playlistContent?.title && (
            <caption className="text-center block text-sm text-gray-500">
              {title || playlistContent?.title}
            </caption>
          )}
        </div>
      </div>
    </NodeViewWrapper>
  );
};

export default VideoBlockViewV1;
